import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Messages from '../Messages';
import { useChat } from '../../modules/ChatPane';
import JUNOSelect from '../JUNOSelect';
import { addEventListener, removeEventListenerByScope } from '../../helpers/pubNubFunctions';
import './style.scss';

export interface JUNOSelectOption {
  name: string;
  value: string;
  notification?: boolean;
}

// Renders and toggles chat/cohort <Messages />
// & listens for notifications to display on
// the dropdown options
export default function MultiMessages({ channeltype, showing, cohorts, cohortChannels }) {
  const { context, setContext } = useChat();
  const { activeMessenger, content, multiMessageOptions } = context;

  // Listen for notifications on the different channels, turn on the notification if it is not already
  useEffect(() => {
    // chat channel
    addEventListener(
      PubNubCenter.ON_MESSAGE,
      (event, data) => {
        if (
          data?.channel == content.roomForChannelType(OTSessionContent.CHANNELTYPE_CHAT) &&
          activeMessenger !== OTSessionContent.CHANNELTYPE_CHAT
        ) {
          let shouldUpdate;
          const newOptions = multiMessageOptions.map(option => {
            if (option.value === `chat` && !option.notification) {
              shouldUpdate = true;
              return {
                ...option,
                notification: true,
              };
            } else {
              return option;
            }
          });
          if (shouldUpdate) {
            setContext(old => {
              return {
                ...old,
                multiMessageOptions: newOptions,
              };
            });
          }
        }
      },
      `chat_pane_multi_cohort_listeners_chat`,
    );
    // cohort channels
    cohortChannels.map((cohort, i) => {
      addEventListener(
        PubNubCenter.ON_MESSAGE,
        (event, data) => {
          if (data?.channel == cohort.room && activeMessenger !== cohort.type) {
            let shouldUpdate;
            const newOptions = multiMessageOptions.map(option => {
              if (option.value === cohort.type && !option.notification) {
                shouldUpdate = true;
                return {
                  ...option,
                  notification: true,
                };
              } else {
                return option;
              }
            });
            if (shouldUpdate) {
              setContext(old => {
                return {
                  ...old,
                  multiMessageOptions: newOptions,
                };
              });
            }
          }
        },
        `chat_pane_multi_cohort_listeners_${i}`,
      );
    });

    return () => {
      removeEventListenerByScope(PubNubCenter.ON_MESSAGE, `chat_pane_multi_cohort_listeners_chat`);
      cohorts.map((cohort, i) => {
        removeEventListenerByScope(PubNubCenter.ON_MESSAGE, `chat_pane_multi_cohort_listeners_${i}`);
      });
    };
  }, [context]);

  // Handler to pass into the select
  const onChange = value => {
    setContext(old => {
      return {
        ...old,
        activeMessenger: value,
      };
    });
  };

  // clear the notification when we go to a channel
  useEffect(() => {
    let shouldUpdate;
    const newOptions = multiMessageOptions.map(option => {
      if (option.value === activeMessenger) {
        shouldUpdate = true;
        return {
          ...option,
          notification: false,
        };
      } else {
        return option;
      }
    });
    if (shouldUpdate) {
      setContext(old => {
        return {
          ...old,
          multiMessageOptions: newOptions,
        };
      });
    }
  }, [activeMessenger]);

  return (
    <motion.div
      variants={messagesVariants}
      animate={showing ? `visible` : `hidden`}
      style={{
        flexDirection: 'column',
        overflow: 'hidden',
        flexGrow: 1,
      }}
    >
      <div className={`cohorts_dropdown_container`}>
        <JUNOSelect onChange={onChange} options={multiMessageOptions} theme={`dark`} />
      </div>
      <Messages
        key={`messages_chat`}
        channeltype={channeltype}
        showVoting={false}
        showing={activeMessenger == 'chat'}
      />
      {cohortChannels.map((cohortChannel, i) => {
        return (
          <Messages
            key={`multiMessages_${i}`}
            channeltype={channeltype}
            showVoting={false}
            room={cohortChannel.room}
            showing={activeMessenger == cohortChannel.type}
          />
        );
      })}
    </motion.div>
  );
}

const messagesVariants = {
  hidden: {
    opacity: 0,
    display: 'none',
  },
  visible: {
    opacity: 1,
    display: 'flex',
  },
};
